import React from 'react';
import {
  Alert, Badge, Button, EmptyState, ErrorState, FilterChip, Table,
} from '@parsina/framework';
import { ImageOff, Store } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { REVIEW_VENDOR, VENDORS_QUERY } from './queries';
import {
  ActionConfirm, Code, Fact, LoadMore, MarketDrawer, NotInList, StatusChip, TableSkeleton,
  useEnumLabel, usePagedList,
} from './shared';

export const VENDOR_STATUSES = ['pending', 'approved', 'rejected', 'suspended'];

/** Which decisions a marketplace status admits (design §6.3 / §13). */
export const VENDOR_ACTIONS = {
  pending: ['approve', 'reject'],
  approved: ['suspend'],
  suspended: ['reinstate'],
  rejected: [],
  not_requested: [],
};

const pickVendors = (d) => d?.marketplaceVendorRequests;

/**
 * View «درخواست‌های فروشندگان» — Archetype B: status chips → table → drawer.
 * `?status=` filters (default pending), `?vendor=<clinicId>` opens the drawer.
 */
export default function VendorsView({ view, go }) {
  const { t, formatDate } = useTranslation();
  const list = usePagedList(VENDORS_QUERY, pickVendors, { status: view.status || 'pending' });
  const selected = view.vendor ? list.rows.find((r) => r.clinicId === view.vendor) : null;

  const columns = [
    {
      key: 'name',
      header: t('marketplace.vendors.columns.name'),
      render: (r) => <span dir="auto" className="font-semibold text-foreground">{r.displayName}</span>,
    },
    { key: 'slug', header: t('marketplace.vendors.columns.slug'), width: '11rem', render: (r) => <Code>{r.slug}</Code> },
    { key: 'city', header: t('marketplace.vendors.columns.city'), width: '8rem', render: (r) => <span dir="auto">{r.city || '—'}</span> },
    { key: 'type', header: t('marketplace.vendors.columns.type'), width: '8rem', render: (r) => <span dir="auto">{r.clinicType || '—'}</span> },
    {
      key: 'status',
      header: t('marketplace.vendors.columns.status'),
      width: '9rem',
      render: (r) => <StatusChip value={r.marketplaceStatus} prefix="marketplace.vendors.status" />,
    },
    { key: 'requestedAt', header: t('marketplace.vendors.columns.requestedAt'), width: '9rem', render: (r) => formatDate(r.createdAt) },
  ];

  return (
    <div data-testid="mk-vendors">
      <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3">
        <div role="group" aria-label={t('marketplace.vendors.filters.label')} className="flex flex-wrap gap-1.5" data-testid="mk-vendors-filter">
          {VENDOR_STATUSES.map((s) => (
            <FilterChip
              key={s}
              label={t(`marketplace.vendors.filters.${s}`)}
              active={(view.status || 'pending') === s}
              onClick={() => go({ status: s, vendor: '' })}
              data-testid={`mk-vendors-filter-${s}`}
            />
          ))}
        </div>
      </div>

      <div
        className="rounded-lg border border-border bg-card transition-opacity"
        style={{ opacity: list.refreshing ? 0.45 : 1 }}
        aria-busy={list.refreshing || undefined}
      >
        {list.loading ? (
          <TableSkeleton cols={6} label={t('marketplace.vendors.loading')} data-testid="mk-vendors-loading" />
        ) : list.error ? (
          <ErrorState
            title={t('marketplace.vendors.error')}
            description={list.error}
            onRetry={list.retry}
            retryLabel={t('common.actions.retry')}
            data-testid="mk-vendors-error"
          />
        ) : (
          <>
            <Table
              dense
              ariaLabel={t('marketplace.vendors.tableAria')}
              columns={columns}
              rows={list.rows}
              rowKey={(r) => r.clinicId}
              onRowActivate={(r) => go({ vendor: r.clinicId })}
              rowProps={(r) => ({ 'data-testid': `mk-vendors-row-${r.clinicId}`, 'aria-label': t('marketplace.vendors.open', { name: r.displayName }) })}
              empty={(
                <EmptyState
                  icon={<Store size={44} strokeWidth={1.5} aria-hidden />}
                  title={t('marketplace.vendors.empty.title')}
                  description={t('marketplace.vendors.empty.desc')}
                  data-testid="mk-vendors-empty"
                />
              )}
              data-testid="mk-vendors-table"
            />
            {list.cursor ? <LoadMore onClick={list.more} pending={list.moreLoading} data-testid="mk-vendors-more" /> : null}
          </>
        )}
      </div>

      {view.vendor ? (
        <VendorDrawer
          vendor={selected}
          onClose={() => go({ vendor: '' })}
          onChanged={(next) => { list.replace((r) => r.clinicId === next.clinicId, next); list.reload(); }}
        />
      ) : null}
    </div>
  );
}

function VendorDrawer({ vendor, onClose, onChanged }) {
  const { t, formatDate, formatPercent } = useTranslation();
  const storeStatus = useEnumLabel('marketplace.vendors.storeStatus');
  const missingLabel = useEnumLabel('marketplace.vendors.missing');
  const [action, setAction] = React.useState(null);
  const [error, setError] = React.useState(null);

  if (!vendor) {
    return (
      <MarketDrawer onClose={onClose} title={t('marketplace.vendors.drawer.title')} data-testid="mk-vendor-drawer">
        <NotInList data-testid="mk-vendor-drawer-missing" />
      </MarketDrawer>
    );
  }

  const actions = VENDOR_ACTIONS[vendor.marketplaceStatus] || [];
  const open = (decision) => {
    setError(null);
    setAction({
      key: decision,
      decision,
      title: t(`marketplace.vendors.confirm.${decision}`, { name: vendor.displayName }),
      body: t(`marketplace.vendors.confirm.${decision}Body`),
      confirmLabel: t(`marketplace.vendors.actions.${decision}`),
      destructive: decision === 'reject' || decision === 'suspend',
      field: decision === 'reject' ? 'rejectNote' : 'note',
      document: REVIEW_VENDOR,
      variables: (note) => ({ clinicId: vendor.clinicId, decision, note }),
      success: t(`marketplace.vendors.success.${decision}`),
    });
  };

  const missing = vendor.readiness?.missing || [];

  return (
    <MarketDrawer
      onClose={onClose}
      title={t('marketplace.vendors.drawer.title')}
      sub={<Code>{vendor.slug}</Code>}
      data-testid="mk-vendor-drawer"
    >
      <div className="flex flex-col gap-5">
        {error ? <Alert variant="error" data-testid="mk-vendor-drawer-error">{t('marketplace.drawer.failed', { message: error })}</Alert> : null}

        <div className="flex items-center gap-3">
          {vendor.logoUrl ? (
            <img src={vendor.logoUrl} alt={t('marketplace.vendors.drawer.logo')} className="h-16 w-16 rounded-lg border border-border object-cover" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground" title={t('marketplace.vendors.drawer.noLogo')}>
              <ImageOff size={24} aria-label={t('marketplace.vendors.drawer.noLogo')} />
            </div>
          )}
          <div className="min-w-0">
            <div dir="auto" className="truncate text-base font-bold text-foreground">{vendor.displayName}</div>
            {vendor.tagline ? <div dir="auto" className="truncate text-sm text-muted-foreground">{vendor.tagline}</div> : null}
            <div className="mt-1"><StatusChip value={vendor.marketplaceStatus} prefix="marketplace.vendors.status" data-testid="mk-vendor-drawer-status" /></div>
          </div>
        </div>

        <section>
          <h2 className="mb-2 text-sm font-bold text-foreground">{t('marketplace.drawer.actions')}</h2>
          {actions.length ? (
            <div className="flex flex-wrap gap-2">
              {actions.map((decision) => (
                <Button
                  key={decision}
                  variant={decision === 'approve' || decision === 'reinstate' ? 'default' : 'destructive'}
                  onClick={() => open(decision)}
                  data-testid={`mk-vendor-${decision}`}
                >
                  {t(`marketplace.vendors.actions.${decision}`)}
                </Button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('marketplace.drawer.noActions')}</p>
          )}
        </section>

        <section>
          <h2 className="mb-2 text-sm font-bold text-foreground">{t('marketplace.vendors.drawer.profile')}</h2>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <Fact label={t('marketplace.vendors.columns.city')} value={vendor.city ? <span dir="auto">{vendor.city}</span> : null} />
            <Fact label={t('marketplace.vendors.columns.type')} value={vendor.clinicType ? <span dir="auto">{vendor.clinicType}</span> : null} />
            <Fact label={t('marketplace.vendors.drawer.storeStatus')} value={storeStatus(vendor.status)} />
            <Fact label={t('marketplace.vendors.columns.requestedAt')} value={vendor.createdAt ? formatDate(vendor.createdAt) : null} />
            <Fact label={t('marketplace.vendors.drawer.phone')} value={vendor.contactPhone ? <Code>{vendor.contactPhone}</Code> : null} />
            <Fact
              label={t('marketplace.vendors.drawer.commission')}
              value={vendor.commissionPct == null ? t('marketplace.vendors.drawer.commissionDefault') : formatPercent(vendor.commissionPct, { maximumFractionDigits: 2 })}
            />
            <Fact label={t('marketplace.vendors.drawer.payout')} value={vendor.payoutAccountId ? <Code>{vendor.payoutAccountId}</Code> : null} />
            <Fact label={t('marketplace.vendors.drawer.reviewedAt')} value={vendor.marketplaceReviewedAt ? formatDate(vendor.marketplaceReviewedAt) : null} />
            {vendor.marketplaceRejectionReason ? (
              <Fact wide label={t('marketplace.vendors.drawer.rejection')} value={<span dir="auto">{vendor.marketplaceRejectionReason}</span>} />
            ) : null}
            <Fact wide label={t('marketplace.vendors.drawer.description')} value={vendor.description ? <span dir="auto" className="whitespace-pre-line font-normal">{vendor.description}</span> : null} />
          </dl>
        </section>

        <section data-testid="mk-vendor-drawer-readiness">
          <h2 className="mb-2 text-sm font-bold text-foreground">{t('marketplace.vendors.drawer.readiness')}</h2>
          {missing.length ? (
            <>
              <p className="mb-2 text-xs text-muted-foreground">{t('marketplace.vendors.drawer.missing')}</p>
              <ul className="flex flex-wrap gap-1.5">
                {missing.map((m) => <li key={m}><Badge variant="warning">{missingLabel(m)}</Badge></li>)}
              </ul>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t('marketplace.vendors.drawer.ready')}</p>
          )}
        </section>
      </div>

      <ActionConfirm
        action={action}
        testId="mk-vendor-dialog"
        onClose={() => setAction(null)}
        onError={setError}
        onDone={(d) => { setError(null); if (d?.reviewMarketplaceVendor) onChanged(d.reviewMarketplaceVendor); }}
      />
    </MarketDrawer>
  );
}
